import { Module } from 'vuex';
import { api } from '../../plugins/salut-api';
import {
	AppUserProfile,
	Contact,
	CreateAppUserDto,
	QrCodeData,
} from '../../types';
import { RootState, VolunteerState } from '../types';

const SET_CURRENT_APP_USER = 'SET_CURRENT_APP_USER';
const SET_DRAFT_APP_USER = 'SET_DRAFT_APP_USER';

export const VolunteerActionKeys = {
	fetchAppUser: 'fetchAppUser',
	fetchAppUsers: 'fetchAppUsers',
	fetchAppUserByQrCode: 'fetchAppUserByQrCode',
	clearAppUser: 'clearAppUser',
	initDraftAppUserFromContact: 'initDraftAppUserFromContact',
	setAppUser: 'setAppUser',
	setDraftAppUser: 'setDraftAppUser',
	unsetDraftAppUser: 'unsetDraftAppUser',
	saveDraftAppUser: 'saveDraftAppUser',
};

export const VolunteerGetterKeys = {
	currentAppUser: 'currentAppUser',
	draftAppUser: 'draftAppUser',
};

const store: Module<VolunteerState, RootState> = {
	namespaced: true,

	state: {
		currentAppUser: undefined,
		currentDraftAppUser: undefined,
	},

	mutations: {
		[SET_CURRENT_APP_USER](state, appUser: AppUserProfile) {
			state.currentAppUser = appUser;
		},

		[SET_DRAFT_APP_USER](state, draftUser: CreateAppUserDto) {
			state.currentDraftAppUser = draftUser;
		},
	},

	actions: {
		async [VolunteerActionKeys.fetchAppUser](
			{ commit },
			{ appUserId }: { appUserId: string },
		) {
			commit(SET_CURRENT_APP_USER, undefined); // clear first
			const user = await api.volunteer.fetchAppUser(appUserId);
			commit(SET_CURRENT_APP_USER, user);
		},

		async [VolunteerActionKeys.fetchAppUserByQrCode](
			{ commit },
			{ contactQrCode, userQrCode }: QrCodeData,
		) {
			commit(SET_CURRENT_APP_USER, undefined); // clear first
			commit(SET_DRAFT_APP_USER, undefined); // clear first
			const users = await api.volunteer.fetchAppUsers({
				contactQrCode,
				userQrCode,
			});
			if (users.length > 0) {
				commit(SET_CURRENT_APP_USER, users[0]);
			}
		},

		[VolunteerActionKeys.clearAppUser]({ commit }) {
			commit(SET_CURRENT_APP_USER, undefined);
		},

		async [VolunteerActionKeys.initDraftAppUserFromContact](
			{ commit },
			{ contact }: { contact: Contact },
		) {
			commit(SET_DRAFT_APP_USER, {
				chineseName: contact.fullNameChi,
				firstName: contact.firstNameEng,
				lastName: contact.lastNameEng,
				email: contact.emails?.find(e => e.type === 'general')?.email,
				mobileNumber: contact.phones?.find(p => p.type === 'mobile')?.phone,
				contactId: contact.id,
			});
		},

		[VolunteerActionKeys.setDraftAppUser](
			{ commit },
			{ appUser }: { appUser: CreateAppUserDto },
		) {
			commit(SET_DRAFT_APP_USER, appUser);
		},

		[VolunteerActionKeys.setAppUser]({ commit }, appUser: AppUserProfile) {
			commit(SET_CURRENT_APP_USER, appUser);
			commit(SET_DRAFT_APP_USER, undefined);
		},

		[VolunteerActionKeys.unsetDraftAppUser]({ commit }) {
			commit(SET_DRAFT_APP_USER, undefined);
		},

		async [VolunteerActionKeys.saveDraftAppUser]({ state }) {
			const appUser = await api.volunteer.createAppUser(
				state.currentDraftAppUser,
			);
			return appUser;
		},
	},

	getters: {
		[VolunteerGetterKeys.currentAppUser]: state => state.currentAppUser,
		[VolunteerGetterKeys.draftAppUser]: state => state.currentDraftAppUser,
	},
};

export default store;
